// Tiny pub/sub store shared by the page (which publishes signals) and the
// globally mounted <LeadBot /> (which decides when to speak).
// No external deps, works on React 17 and 18.
import { useEffect, useState } from 'react';

const SESSION_KEY = 'rc_leadbot_session';
const SNOOZE_KEY = 'rc_leadbot_snooze_until';

const readSession = () => {
    try { return JSON.parse(sessionStorage.getItem(SESSION_KEY)) || {}; } catch { return {}; }
};

const session = readSession();

const INITIAL_PAGE_SIGNALS = {
    page: null,                 // 'search' while SearchForm is mounted, 'booking' on booking pages
    search: null,               // contactSearchData of the current results
    searchKey: '',
    resultsLoading: false,
    resultsCount: 0,
    noRoutes: false,
    allWaitlisted: false,
    tatkalWindow: false,
    dateFlips: 0,
    deepScrollNoBook: false,
    returnedAfterAway: false,
    suppressed: {},             // reason -> bool (panel open, modal open, ...)

    // booking pages (useBookingLeadSignals)
    booking: null,              // { mode, stage, src_name, dest_name, travel_date, amount, rcBookingId, prefillPhone, key }
    bookingKey: '',
    bookingFailed: false,       // post-payment: ticket failed / booking not found / verification timed out
    paymentFailed: false,
    bookError: false,           // fare quote / seat layout failed to load
    paymentDismissed: false,    // payment started, then closed without paying
    formIdle: false,            // sat on the passenger form without doing anything
    bookReturned: false,        // came back after a while in another tab (comparing prices?)
    dockOffset: 0,              // px to lift the dock above a fixed page footer
    leaveRequest: null,         // { id, trigger, onLeave, onStay } while the exit survey is asking
};

let state = {
    ...INITIAL_PAGE_SIGNALS,
    leadSubmitted: !!session.leadSubmitted,
    nudgesShown: session.nudgesShown || 0,
    nudgedKeys: session.nudgedKeys || [],
    bookingLeadKeys: session.bookingLeadKeys || [],   // bookings we already took a callback for
    reasonKeys: session.reasonKeys || [],             // bookings that already told us why they left
    botAvailable: false,                              // LeadBot mounted, enabled and on a booking page
};

const listeners = new Set();

const persist = () => {
    try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({
            leadSubmitted: state.leadSubmitted,
            nudgesShown: state.nudgesShown,
            nudgedKeys: state.nudgedKeys.slice(-20),
            bookingLeadKeys: state.bookingLeadKeys.slice(-20),
            reasonKeys: state.reasonKeys.slice(-20),
        }));
    } catch { /* storage blocked */ }
};

const emit = () => listeners.forEach(l => l(state));

export const getLeadBotState = () => state;

export const setLeadSignals = (partial) => {
    const changed = Object.keys(partial).some(k => state[k] !== partial[k]);
    if (!changed) return;
    state = { ...state, ...partial };
    emit();
};

export const setLeadBotSuppressed = (reasons) => {
    const next = { ...state.suppressed, ...reasons };
    const same = Object.keys(next).every(k => !!next[k] === !!state.suppressed[k]);
    if (same) return;
    state = { ...state, suppressed: next };
    emit();
};

export const resetPageSignals = () => {
    state = { ...state, ...INITIAL_PAGE_SIGNALS };
    emit();
};

export const bumpDateFlips = () => setLeadSignals({ dateFlips: state.dateFlips + 1 });

export const markLeadSubmitted = () => {
    state = { ...state, leadSubmitted: true };
    persist();
    emit();
};

const addKey = (list, key) => [...list.filter(k => k !== key), key];

export const markBookingLeadSubmitted = (key) => {
    if (!key) return;
    state = { ...state, bookingLeadKeys: addKey(state.bookingLeadKeys, key) };
    persist();
    emit();
};

export const markReasonGiven = (key) => {
    if (!key || state.reasonKeys.includes(key)) return;
    state = { ...state, reasonKeys: addKey(state.reasonKeys, key) };
    persist();
    emit();
};

export const recordNudge = (key) => {
    state = {
        ...state,
        nudgesShown: state.nudgesShown + 1,
        nudgedKeys: [...state.nudgedKeys, key],
    };
    persist();
    emit();
};

export const snoozeLeadBot = (hours = 24) => {
    try { localStorage.setItem(SNOOZE_KEY, String(Date.now() + hours * 3600 * 1000)); } catch { /* ignore */ }
    emit();
};

export const isLeadBotSnoozed = () => {
    try { return Number(localStorage.getItem(SNOOZE_KEY) || 0) > Date.now(); } catch { return false; }
};

export const setBotAvailable = (v) => {
    if (state.botAvailable === !!v) return;
    state = { ...state, botAvailable: !!v };
    emit();
};

/**
 * Booking pages call this instead of leaving directly (close button, back press).
 * If the bot can ask "what stopped you?", it opens the exit survey and calls onLeave
 * once the user is done (or onStay if they decide to keep booking).
 * Otherwise onLeave runs right away. Returns true when the bot intercepted.
 */
export const requestLeave = (onLeave, { trigger = 'bookLeave', onStay } = {}) => {
    const suppressed = Object.values(state.suppressed || {}).some(Boolean);
    const can = state.botAvailable
        && state.page === 'booking'
        && !suppressed
        && !state.leaveRequest
        && !isLeadBotSnoozed()
        && !state.reasonKeys.includes(state.bookingKey)
        && !state.bookingLeadKeys.includes(state.bookingKey);
    if (!can) {
        try { if (onLeave) onLeave(); } catch { /* ignore */ }
        return false;
    }
    state = { ...state, leaveRequest: { id: Date.now(), trigger, onLeave, onStay } };
    emit();
    return true;
};

/** action: 'leave' runs onLeave, 'stay' runs onStay, 'drop' just clears the request. */
export const resolveLeaveRequest = (action = 'leave') => {
    const req = state.leaveRequest;
    if (!req) return;
    state = { ...state, leaveRequest: null };
    emit();
    try {
        if (action === 'leave' && req.onLeave) req.onLeave();
        if (action === 'stay' && req.onStay) req.onStay();
    } catch { /* ignore */ }
};

export const useLeadBotState = () => {
    const [s, setS] = useState(state);
    useEffect(() => {
        listeners.add(setS);
        setS(state);
        return () => { listeners.delete(setS); };
    }, []);
    return s;
};
